import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  billingAccountMsg,
  changeRequestorMsg,
  detailsChangeMsg,
  statusMsg,
  t,
  theChangeFeeMsg,
  timeOfChangeMsg,
  topicMsg,
  userMsg,
} from '../../common/i18n/index.js';
import { formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils';
import { getPriceAsText } from '../../common/utils/priceUtils';
import { CustomerOrderProductType, CustomerOrderTopic } from '../../generated/api/models.js';
import { getTopicText } from '../CustomerOrderList/customerOrderUtils';
import { Status } from '../Status/Status.js';
import { ChangeRequestInfoLink } from './ChangeRequestInfoLink';
import { getCustomerOrderStatusInfo } from './CustomerOrderDetailsUtil.js';
import { NONE_TEXT, getChangeRequestChangedFields, getChangeRequestPriceFields } from './changeRequestUtils';
import type { CLDescriptionItem } from '../../common/types/componentLibrary';
import type { ChangeRequestDetails } from '../../generated/api/models.js';

import './ChangeRequestInfo.scss';

interface CustomerOrderInfoParams {
  changeRequest: ChangeRequestDetails;
  mdmId?: string;
}

const PriceDescription = ({ serviceFee }: { serviceFee: number }) => {
  const isNegative = serviceFee < 0;
  const priceText = getPriceAsText(Math.abs(serviceFee));
  const finalPriceText = isNegative ? `-${priceText}` : priceText;

  return (
    <>
      {finalPriceText}
      <br />
      <small>{serviceFee > 0 && t.PLPN('Change fee will be added to the next invoice')}</small>
    </>
  );
};

const ChangeRequestBasicInfo = ({ changeRequest }: { changeRequest: ChangeRequestDetails }) => {
  const customerOrderStatusInfo = getCustomerOrderStatusInfo(changeRequest.status);

  const fields: CLDescriptionItem[] = [
    {
      title: t.ASQT(statusMsg),
      description: <Status color={customerOrderStatusInfo.color} textAfterBadge={customerOrderStatusInfo.text} />,
    },
  ];

  fields.push({
    title: t.Q1DM(timeOfChangeMsg),
    description: formatTimestampToDDMMYYYY(changeRequest.created),
  });

  if (changeRequest.serviceFee !== undefined) {
    fields.push({
      title: t.CMFZ(theChangeFeeMsg),
      description: <PriceDescription serviceFee={changeRequest.serviceFee} />,
    });
  }

  if (changeRequest.orderingContact !== undefined) {
    fields.push({
      title: t.C8M7(changeRequestorMsg),
      description: changeRequest.orderingContact,
    });
  }

  if (changeRequest.topic !== undefined) {
    fields.push({
      title: t.JJTS(topicMsg),
      description: getTopicText(changeRequest.topic),
    });
  }

  return <CL.Description items={fields} className={dsClass.PADDING_BOTTOM_5} />;
};

const getChangeTargetDescription = (changeRequest: ChangeRequestDetails, mdmId?: string) => {
  if (
    changeRequest.productType &&
    changeRequest.subscriptionOrDeviceInfo &&
    changeRequest.subscriptionOrDeviceLink &&
    changeRequest.productType !== CustomerOrderProductType.OTHER
  ) {
    return <ChangeRequestInfoLink changeRequest={changeRequest} mdmId={mdmId} />;
  }
  return changeRequest.subscriptionOrDeviceInfo ?? NONE_TEXT;
};

const getChangeTargetField = (changeRequest: ChangeRequestDetails, mdmId?: string): CLDescriptionItem => {
  if (changeRequest.productType === CustomerOrderProductType.DEVICE) {
    return {
      title: t.CJGP('The device affected by the change'),
      description: getChangeTargetDescription(changeRequest, mdmId),
    };
  }

  return {
    title: t.UPZV('The subscription affected by the change'),
    description: getChangeTargetDescription(changeRequest, mdmId),
  };
};

const ChangeRequestUserInfo = ({ userName, userEmail }: { userName: string; userEmail?: string }) => (
  <>
    {userName}
    {userEmail !== undefined ? (
      <>
        <br />
        {userEmail}
      </>
    ) : null}
  </>
);

const ChangeRequestDetailedInfo = ({
  changeRequest,
  mdmId,
}: {
  changeRequest: ChangeRequestDetails;
  mdmId?: string;
}) => {
  const { billingAccount, userName, userEmail } = changeRequest;

  const fields: CLDescriptionItem[] = [];

  // If the change request is about EPP extension period end, we don't show the change target field, since sub cannot be found anymore
  if (changeRequest.topic !== CustomerOrderTopic.EPP_EXTENSION_PERIOD_END) {
    fields.push(getChangeTargetField(changeRequest, mdmId));
  }

  if (userName !== undefined) {
    fields.push({
      title: t.LUZM(userMsg),
      description: <ChangeRequestUserInfo userName={userName} userEmail={userEmail} />,
    });
  }

  if (billingAccount !== undefined) {
    fields.push({
      title: t.IFT9(billingAccountMsg),
      description: billingAccount,
    });
  }

  const priceFields: CLDescriptionItem[] = getChangeRequestPriceFields(changeRequest);
  if (priceFields.length > 0) {
    fields.push(...priceFields);
  }

  const changedFields: CLDescriptionItem[] = getChangeRequestChangedFields(changeRequest);
  if (changedFields.length > 0) {
    fields.push(...changedFields);
  }

  return (
    <>
      <h3 className={`${dsClass.MARGIN_BOTTOM_5} ${dsClass.MARGIN_TOP_6}`}>{t.E9BD(detailsChangeMsg)}</h3>
      <CL.Description items={fields} className={dsClass.PADDING_BOTTOM_5} />
    </>
  );
};

export const ChangeRequestInfo = ({ changeRequest, mdmId }: CustomerOrderInfoParams) => (
  <div className="change-request-info">
    <ChangeRequestBasicInfo changeRequest={changeRequest} />
    <hr />
    <ChangeRequestDetailedInfo changeRequest={changeRequest} mdmId={mdmId} />
  </div>
);
